import { BaseComponent } from './BaseComponent';
import { PrintManager } from './PrintManager';
import { KeepClientAliveSentinel } from './KeepClientAliveSentinel';

import { IServiceClient, IReportController } from './Interfaces';

import { ParameterValuesType, LocalizationMessageType } from './Types';

import {
    DeviceInfo,
    DocumentInfo,
    PageInfo,
    ParameterInfo,
    PageActionEventArgs,
    PageTargetElement,
    BookmarkNode,
    EmailInfo,
    SearchInfo,
    SearchResult,
    RenderingExtension } from './Models';

import { PrintMode, ViewMode, ScaleMode, PageMode } from './Enums';

import { escapeHtml } from './Utils/DomUtils';
import {
    isExceptionOfType,
    isInternalServerError,
    isInvalidClientException,
    isInvalidParameterException,
    stringFormat
} from './Utils/Utils';
import { ExportStartEventArgs } from './Models/EventArgs/ExportStartEventArgs';
import { TooltipEventArgs } from './Models/TooltipEventArgs';
import { PrintStartedEventArgs } from './Models/EventArgs/PrintStartedEventArgs';
import { PrintDocumentReadyEventArgs } from './Models/EventArgs/PrintDocumentReadyEventArgs';
import { ExportStartedEventArgs } from './Models/EventArgs/ExportStartedEventArgs';
import { ExportDocumentReadyEventArgs } from './Models/EventArgs/ExportDocumentReadyEventArgs';
import { SendEmailStartedEventArgs } from './Models/EventArgs/SendEmailStartedEventArgs';
import { SendEmailDocumentReadyEventArgs } from './Models/EventArgs/SendEmailDocumentReadyEventArgs';
import { RequestError } from './Models/RequestError';
import { ParameterValidators } from './Utils/ParameterValidators';
import { ConfigurationInfo } from './Types/ConfigurationInfoType';

const DocumentInfoPollIntervalMs: number = 500;
const PagePollIntervalMs: number = 500;

function addSingleComboParam(processedParam, paramValue, key) {
    try {
        const nameValuePair = processedParam.availableValues.find(obj => {
            return obj['value'] === paramValue;
        });

        if (!nameValuePair) {
            throw new Error(`The available values of parameter ${key} do not contain Value property that equals ${paramValue}`);
        }

        return { valueMember: paramValue, displayMember: nameValuePair['name'] };

    } catch (e) {
        // utils.logError(e);
        return undefined;
    }
}

function addMultiComboParam(processedParam, paramValue, key) {
    const paramArr = [];

    for (let i in paramValue) {
        paramArr.push(addSingleComboParam(processedParam, paramValue[i], key));
    }

    return paramArr;
}

export class ReportSourceOptions {
    public report: string;
    public parameters: ParameterValuesType;

    constructor(report: string = '', parameters: ParameterValuesType = {}) {
        this.report = report;
        this.parameters = parameters;
    }
}

export class ReportControllerOptions {
    public keepClientAlive: boolean = false;
    public authenticationToken: string;
    public reportSource: ReportSourceOptions | null;
    public printMode: PrintMode;
    public pageMode: PageMode;
    public viewMode: ViewMode;
    public scaleMode: ScaleMode;
    public scale: number;
    public messages: LocalizationMessageType;
    public enableAccessibility: boolean;
    public searchMetadataOnDemand: boolean;

    // eslint-disable-next-line max-params
    constructor(
        keepClientAlive: boolean,
        authenticationToken: string,
        reportSource: ReportSourceOptions,
        printMode: PrintMode,
        pageMode: PageMode,
        viewMode: ViewMode,
        scaleMode: ScaleMode,
        scale: number,
        messages: LocalizationMessageType,
        enableAccessibility: boolean = false,
        searchMetadataOnDemand: boolean = false
    ) {
        this.keepClientAlive = keepClientAlive;
        this.authenticationToken = authenticationToken;
        this.reportSource = reportSource;
        this.printMode = printMode;
        this.pageMode = pageMode;
        this.viewMode = viewMode;
        this.scaleMode = scaleMode;
        this.scale = scale;
        this.messages = messages;
        this.enableAccessibility = enableAccessibility;
        this.searchMetadataOnDemand = searchMetadataOnDemand;
    }
}

export class ReportController extends BaseComponent implements IReportController {
    private configurationInfo: ConfigurationInfo = null;
    private keepClientAliveSentinel: KeepClientAliveSentinel | null = null;
    private serviceClient: IServiceClient;
    private printManager: PrintManager;
    private registerClientPromise: Promise<string | void> | null = null;
    private registerInstancePromise: Promise<string | void> | null = null;
    private documentFormatsPromise: Promise<RenderingExtension[] | void> | null = null;
    private clientId: string = '';
    private reportInstanceId: string = '';
    private documentId: string = '';
    private threadId: string = '';
    private parameterValues: ParameterValuesType = {};
    private bookmarkNodes: BookmarkNode[] = [];
    private renderingExtensions: RenderingExtension[] | null = null;
    private pageCount: number = 0;
    private currentPageNumber: number = 0;
    private clientHasExpired: boolean = false;
    private cancelLoad: boolean = false;
    private searchInitiated: boolean = false;
	private aiPromptInitiated: boolean = false;
    private contentTabIndex: number = 0;
    private respectAutoRun: boolean = true;

    private options: ReportControllerOptions;
    private processedParameterValues: Object = {};

    constructor(serviceClient: IServiceClient, options: ReportControllerOptions) {
        super();

        this.options = options;

        if (options.reportSource) {
            this.setParameters(options.reportSource.parameters);
        }

        this.printManager = new PrintManager();

        this.serviceClient = serviceClient;

        if (options.authenticationToken) {
            this.serviceClient.setAccessToken(options.authenticationToken);
        }
    }

    get autoRunEnabled(): boolean {
        var reportAutoRunParameter = this.parameterValues && 'trv_AutoRun' in this.parameterValues ? this.parameterValues['trv_AutoRun'] : true;
        return !this.respectAutoRun || reportAutoRunParameter;
    }

    destroy() {
        if (this.printManager) {
            this.printManager.destroy();
        }
    }

    setOptions(options: ReportControllerOptions) {
        this.clearReportState();
        this.clearClientId();

        this.options = options;
        if (this.options.reportSource) {
            this.setParameters(this.options.reportSource.parameters);
        }
    }

    setAuthenticationToken(token: string) {
        this.serviceClient.setAccessToken(token);
    }

    init() {
        /*
        TODO: Pass version as parameter.

        this.getServiceVersion().then((version: string) => {
            if (process.env['PRODUCTION'] && version !== process.env['VERSION']) {
                this.emit('reportVersionMismatch');

                Promise.reject();
            }

            this.refreshReport(true, '');
        });
        */

        this.refreshReport(true, '');
    }

    getServiceVersion() {
        return this.serviceClient.getServiceVersion();
    }

    getServiceConfiguration() {
        return this.serviceClient.getServiceConfiguration();
    }

    stopRendering() {
        this.serviceClient
            .deleteReportDocument(this.clientId, this.reportInstanceId, this.documentId)
            .catch((error: RequestError) => {
                this.handleRequestError(error);
            })
            .then(() => {
                this.cancelLoad = true;

                this.resetPageNumbers();
                this.emit('renderingStopped');
            });
    }

    refreshReport(ignoreCache: boolean, baseDocumentId: string, alwaysRespectAutoRun: boolean = false) {
        this.emit('loadedReportChange');

        this.cancelLoad = false;
        this.respectAutoRun = alwaysRespectAutoRun || this.respectAutoRun;

        if (this.clientHasExpired) {
            this.clientHasExpired = false;
            this.clearClientId();
        }

        if (this.getReport() == '') {
            this.emit('noReport');
            return;
        }

        let loadParametersPromise = this.loadParameters();

        loadParametersPromise.then((result: ParameterInfo[]) => {
            this.emit('parametersLoaded', result);

            this.processParameters(result, ignoreCache, baseDocumentId);
        });

        this.emit('reloadParameters', loadParametersPromise);
    }

    processParameters(result: ParameterInfo[], ignoreCache: boolean, baseDocumentId: string) {
        let parameterValues: ParameterValuesType = {};
        let invalidParameters: ParameterInfo[] = [];

        let hasError = false;
        for (let parameter of result) {
            try {
                let value = ParameterValidators.validate(parameter, parameter.value);
                parameterValues[parameter.id] = value;
            } catch (e) {
                hasError = true;
                invalidParameters.push(parameter);
            }
        }

        if (hasError) {
            this.missingOrInvalidParameters(invalidParameters);
        } else {
            this.setParameters(parameterValues);

            if (this.autoRunEnabled) {
                this.refreshReportCore(ignoreCache, baseDocumentId);
            } else {
                this.emitAsync('reportAutoRunOff');
            }
        }
    }

    hasInvalidParameter(parameters: ParameterInfo[]): boolean {
        for (const parameter of parameters) {
            try {
                ParameterValidators.validate(parameter, parameter.value)
            } catch {
                return true;
            }
        }

        return false;
    }

    refreshReportCore(ignoreCache: boolean, baseDocumentId: string = '', actionId: string = '') {
        this.loadReport(ignoreCache, baseDocumentId, actionId);
    }

    navigateToPage(page: number, targetElement: PageTargetElement | undefined) {
        this.emit('navigateToPage', page, targetElement);
    }

    getReportDocumentId() {
        return this.documentId;
    }

    setReportDocumentId(documentId: string) {
        this.documentId = documentId;
    }

    getReportPage(page: number) {
        this.getPage(page).then(this.onPageReady.bind(this));
    }

    getPageData(page: number): Promise<PageInfo> {
        return this.getPage(page);
    }

    setParameters(values: ParameterValuesType) {
        this.parameterValues = values;
    }

    setParameter(id: string, value: any) {
        this.parameterValues[id] = value;
    }

    getReportSource(): ReportSourceOptions | null {
        return new ReportSourceOptions(
            this.options.reportSource?.report,
            this.parameterValues
        );
    }

    setReportSource(rs: ReportSourceOptions) {
        this.options.reportSource = rs;
        this.setParameters(this.options.reportSource.parameters);

        this.emit('reportSourceChanged', rs);
    }

    previewReport() {
        this.emit('loadedReportChange');
        this.respectAutoRun = false;
        this.refreshReportCore(false);
    }

    getClientId() {
        return this.clientId;
    }

    getScale(): number {
        return this.options.scale;
    }

    setScale(scale: number) {
        if (scale !== this.options.scale) {
            this.options.scale = scale;

            this.emit('scaleChanged', scale);
        }
    }

    getScaleMode(): ScaleMode {
        return this.options.scaleMode;
    }

    setScaleMode(scaleMode: ScaleMode) {
        if (scaleMode !== this.options.scaleMode) {
            this.options.scaleMode = scaleMode;

            this.emit('scaleModeChanged', scaleMode);
        }
    }

    getViewMode(): ViewMode {
        return this.options.viewMode;
    }

    setViewMode(viewMode: ViewMode) {
        if (this.options.viewMode !== viewMode) {
            this.options.viewMode = viewMode;

            if (this.options.reportSource) {
                this.emit('viewModeChanged', viewMode);
            }
        }
    }

    getPageMode(): PageMode {
        return this.options.pageMode;
    }

    setPageMode(pageMode: PageMode) {
        if (this.options.pageMode !== pageMode) {
            this.options.pageMode = pageMode;

            if (this.options.reportSource) {
                this.emit('pageModeChanged', pageMode);
            }
        }
    }

    getPrintMode(): PrintMode {
        return this.options.printMode;
    }

    setPrintMode(printMode: PrintMode) {
        if (this.options.printMode !== printMode) {
            this.options.printMode = printMode;

            this.emit('printModeChanged', printMode);
        }
    }

    getCurrentPageNumber(): number {
        return this.currentPageNumber;
    }

    setCurrentPageNumber(number: number) {
        if (this.currentPageNumber !== number) {
            this.currentPageNumber = number;

            this.emit('currentPageChanged', number);
        }
    }

    getPageCount(): number {
        return this.pageCount;
    }

    scrollPageReady() {
        this.emit('scrollPageReady');
    }

    executeReportAction(interactiveActionArgs: PageActionEventArgs) {
        let action = interactiveActionArgs.action;
        window.setTimeout(() => {
            this.emit('interactiveActionExecuting', interactiveActionArgs);

            if (!interactiveActionArgs.cancel) {
                if (action.Type === 'navigateToReport') {
                    this.emit('serverActionStarted');

                    let args = action.Value;
                    let parameters = this.fixDataContractJsonSerializer(args.ParameterValues);
                    this.respectAutoRun = true;
                    this.emit('navigateToReport', { Report: args.Report, Parameters: parameters });
                } else if (action.Type === 'navigateToBookmark') {
                    let page = this.getPageForBookmark(this.bookmarkNodes, action.Value);
                    let target = new PageTargetElement(action.Value, 'bookmark');
                    this.navigateToPage(page, target);
                } else if (action.Type === 'sorting') {
                    this.execServerAction(action.Id);
                } else if (action.Type === 'toggleVisibility') {
                    this.execServerAction(action.Id);
                } else if (action.Type === 'navigateToUrl') {
                    let args = action.Value;
                    window.open(args.Url, args.Target);
                } else if (action.Type === 'customAction') {
                    // do nothing
                }
            }
        }, 0);
    }

    reportActionEnter(interactiveActionArgs: PageActionEventArgs) {
        this.emit('interactiveActionEnter', interactiveActionArgs);
    }

    reportActionLeave(interactiveActionArgs: PageActionEventArgs) {
        this.emit('interactiveActionLeave', interactiveActionArgs);
    }

    reportTooltipOpening(tooltipEventArgs: TooltipEventArgs) {
        this.emit('toolTipOpening', tooltipEventArgs);
    }

    reportTooltipClosing(tooltipEventArgs: TooltipEventArgs) {
        this.emit('toolTipClosing', tooltipEventArgs);
    }

    async printReport() {
        let deviceInfo = this.createDeviceInfo();
        deviceInfo.ImmediatePrint = true;

        let printStartedEventArgs = new PrintStartedEventArgs(deviceInfo);

        this.emit('printStarted', printStartedEventArgs);

        if (printStartedEventArgs.handled) {
            return;
        }

        this.setUIState('PrintInProgress', true);

        this.exportAsync('PDF', deviceInfo).then((documentId: string) => {
            let url = this.serviceClient.getDocumentUrl(this.clientId, this.reportInstanceId, documentId);

            let canUsePlugin = this.getCanUsePlugin();
            let contentDisposition = canUsePlugin ? 'inline' : 'attachment';
            let queryString = 'response-content-disposition=' + contentDisposition;

            url += `?${queryString}`;

            let printDocumentReadyEventArgs = new PrintDocumentReadyEventArgs(url);

            this.emit('printDocumentReady', printDocumentReadyEventArgs);

            this.setUIState('PrintInProgress', false);

            if (!printDocumentReadyEventArgs.handled) {
                this.printManager.print(url);
            }
        });
    }

    async exportReport(format: string) {
        let deviceInfo = this.createDeviceInfo();

        let eventArgs = new ExportStartEventArgs(deviceInfo, format);

        await this.emitAsync('exportStart', eventArgs);

        if (!eventArgs.isCancelled) {
            let exportStartedEventArgs = new ExportStartedEventArgs(deviceInfo, format);

            this.emit('exportStarted', exportStartedEventArgs);

            if (exportStartedEventArgs.handled) {
                return;
            }

            this.setUIState('ExportInProgress', true);

            this.exportAsync(format, eventArgs.deviceInfo).then(async (documentId: string) => {
                let url = this.serviceClient.getDocumentUrl(this.clientId, this.reportInstanceId, documentId);

                url += '?response-content-disposition=attachment';

                let exportEndEventArgs = new ExportDocumentReadyEventArgs(url, format, '_self');

                await this.emitAsync('exportEnd', exportEndEventArgs);

                this.emit('exportDocumentReady', exportEndEventArgs);

                this.setUIState('ExportInProgress', false);

                if (!exportEndEventArgs.handled) {
                    window.open(url, exportEndEventArgs.windowOpenTarget);
                }
            });
        }
    }

    sendReport(mailInfo: EmailInfo) {
        let deviceInfo = this.createDeviceInfo();

        let sendEmailStartedEventArgs = new SendEmailStartedEventArgs(deviceInfo, mailInfo.format);

        this.emit('sendEmailStarted', sendEmailStartedEventArgs);

        if (sendEmailStartedEventArgs.handled) {
            return;
        }

        this.exportAsync(mailInfo.format, deviceInfo)
            .then((documentId: string) => {
                let url = this.serviceClient.getDocumentUrl(this.clientId, this.reportInstanceId, documentId);

                url += '?response-content-disposition=attachment';

                let sendEmailDocumentReadyEventArgs = new SendEmailDocumentReadyEventArgs(mailInfo, deviceInfo, url);

                this.emit('sendEmailDocumentReady', sendEmailDocumentReadyEventArgs);

                if (!sendEmailDocumentReadyEventArgs.handled) {
                    this.sendDocumentAsync(documentId, mailInfo);
                }
            });
    }

    getSearchResults(searchInfo: SearchInfo): Promise<SearchResult[]> {
        return this.serviceClient
            .getSearchResults(this.clientId, this.reportInstanceId, this.documentId, searchInfo)
            .catch(this.handleSearchRequestError.bind(this))
            .then((result: SearchResult[] | void) => result || []);
    }

    createAIThread() {
        return this.serviceClient
            .createAIThread(this.clientId, this.reportInstanceId, this.getReport(), this.parameterValues)
            .catch((error) => this.raiseError(error._responseJSON.exceptionMessage, false))
            .then((result: any) =>  {
                this.threadId = result?.threadId || '';
                return result;
            });
    }

    getAIResponse(query: string): Promise<string> {
        return this.serviceClient
            .getAIResponse(this.clientId, this.reportInstanceId, this.documentId, this.threadId, query)
            .then((result: string) => result || '');
    }

    private exportAsync(format: string, deviceInfo: DeviceInfo): Promise<string> {
        return this.initializeClient()
            .then(this.registerInstance.bind(this))
            .then(() => {
                return this.registerDocumentAsync(format, deviceInfo, false, this.documentId);
            })
            .then(async (documentId: string) => {
                await this.getDocumentInfo(false, documentId)
                    .catch(errorMessage => this.raiseError(errorMessage));

                return documentId;
            });
    }

    private sendDocumentAsync(documentId: string, args: any) {
        return this.serviceClient
            .sendDocument(this.clientId, this.reportInstanceId, documentId, args)
            .catch((error: RequestError) => {
                this.handleRequestError(
                    error,
                    stringFormat(
                        this.options.messages['ReportViewer_ErrorSendingDocument'],
                        escapeHtml(this.getReport())
                    )
                );
            });
    }

    loadParameters(parameters: ParameterValuesType | undefined = undefined): Promise<any> {
        return this.initializeClient().then(this.getParameters.bind(this, parameters));
    }

    private loadReport(ignoreCache: boolean, baseDocumentId: string, actionId: string) {
        this.clearReportState();

        const format = this.getFormat();
        const deviceInfo = this.createPreviewDeviceInfo();

        this.emitAsync('beforeLoadReport', { deviceInfo: deviceInfo })
            .then(() => this.initializeClient())
            .then(this.registerInstance.bind(this))
            .then(() => this.registerDocumentAsync(format, deviceInfo, ignoreCache, baseDocumentId, actionId))
            .then((documentId: string) => {
                this.documentId = documentId;

                return this.getDocumentInfo(true, this.documentId);
            })
            .then(this.onReportLoadComplete.bind(this))
            .catch(errorMessage => this.raiseError(errorMessage));
    }

    private initializeAndStartSentinel() {
        if (this.options.keepClientAlive && this.clientId) {
            this.serviceClient.getClientsSessionTimeoutSeconds().then((sessionTimeout: number) => {
                this.keepClientAliveSentinel = new KeepClientAliveSentinel(
                    this.serviceClient,
                    this.clientId,
                    sessionTimeout
                );
                this.keepClientAliveSentinel.start();
            });
        }
    }

    private stopSentinel() {
        if (this.keepClientAliveSentinel != null) {
            this.keepClientAliveSentinel.stop();
        }
    }

    private setClientId(id: string | void) {
        this.clientId = id || '';
        this.stopSentinel();
        this.emit('clientIdChanged', this.clientId);
        this.initializeAndStartSentinel();
    }

    private clearClientId() {
        this.clientId = '';
        this.registerClientPromise = null;
        this.stopSentinel();
        this.keepClientAliveSentinel = null;
        this.emit('clientIdChanged', this.clientId);
    }

    private setReportInstanceId(id: string) {
        this.reportInstanceId = id;
    }

    private clearReportInstanceId() {
        this.reportInstanceId = '';
        this.registerInstancePromise = null;
    }

    private clearReportState() {
        this.cancelLoad = false;
        this.documentId = '';
        this.resetPageNumbers();

        this.clearReportInstanceId();
    }

    private resetPageNumbers() {
        this.pageCount = 0;
        this.currentPageNumber = 0;
    }

    private handleSearchRequestError(error: RequestError) {
        if (!isExceptionOfType(error, 'System.ArgumentException')) {
            this.handleRequestError(error, '', true);

            throw null;
        }

        this.throwPromiseError(error);
    }

    private throwPromiseError(error: RequestError) {
        let errorMessage =
            error.responseJSON && error.responseJSON.exceptionMessage
                ? error.responseJSON.exceptionMessage
                : this.options.messages['ReportViewer_PromisesChainStopError'];

        throw errorMessage;
    }

    private handleRequestError(
        requestError: RequestError,
        localizedMessage: string = '',
        suppressErrorBubbling: boolean = false
    ) {
        if (isInvalidClientException(requestError)) {
            this.onClientExpired();
        }

        // Try trimming the Internal Server Error text.
        let errorMessage = isInternalServerError(requestError.error) ? '' : requestError.error;
        let formattedError = this.formatRequestError(requestError, errorMessage, localizedMessage);

        this.raiseError(formattedError);

        if (!suppressErrorBubbling) {
            this.throwPromiseError(requestError);
        }
    }

    private formatRequestError(requestError: RequestError, errorMessage: string, localizedMessage: string) {
        let parsedData: any = requestError.responseJSON;
        let result: string = '';

        if (parsedData) {
            if (isInvalidParameterException(requestError)) {
                return this.options.messages['ReportViewer_MissingOrInvalidParameter'];
            }

            result = escapeHtml(parsedData.message);

            let exceptionMessage = escapeHtml(parsedData.exceptionMessage || parsedData.error_description);
            if (exceptionMessage) {
                if (result) {
                    result += ' ' + exceptionMessage;
                } else {
                    result = exceptionMessage;
                }
            }
        } else {
            result = escapeHtml(requestError.responseText);
        }

        if (localizedMessage || errorMessage) {
            if (result) {
                result = ' ' + result;
            }

            result = escapeHtml(localizedMessage ? localizedMessage : errorMessage) + result;
        }

        if (isInvalidClientException(requestError)) {
            result += '<br />' + this.options.messages['ReportViewer_ClientExpired'];
        }

        return result;
    }

    private raiseError(message: string, shouldClearReport: boolean = true) {
        this.emit('error', message, shouldClearReport);
    }

    private onClientExpired() {
        this.clientHasExpired = true;
        this.emit('clientExpired');
    }

    private initializeClient() {
        if (!this.registerClientPromise) {
            this.registerClientPromise = this.serviceClient
                .registerClient()
                .catch((error: RequestError) => {
                    const message = stringFormat(this.options.messages['ReportViewer_ErrorServiceUrl'], [this.serviceClient.getServiceUrl()]);
                    this.handleRequestError(error, message);
                })
                .then(this.setClientId.bind(this))
                .catch(this.clearClientId.bind(this));
        }

        return this.registerClientPromise;
    }

    private registerInstance() {
        if (!this.registerInstancePromise) {
            this.registerInstancePromise = this.serviceClient
                .createReportInstance(this.clientId, this.getReport(), this.parameterValues)
                .then(this.setReportInstanceId.bind(this))
                .catch(this.clearReportInstanceId.bind(this));
        }

        return this.registerInstancePromise;
    }

    // eslint-disable-next-line max-params
    private async registerDocumentAsync(
        format: string,
        deviceInfo: DeviceInfo,
        ignoreCache: boolean,
        baseDocumentId: string = '',
        actionId: string = ''
    ): Promise<string> {
        let documentId = await this.serviceClient
            .createReportDocument(
                this.clientId,
                this.reportInstanceId,
                format,
                deviceInfo,
                !ignoreCache,
                baseDocumentId,
                actionId
            )
            .catch((error: RequestError) => {
                this.handleRequestError(
                    error,
                    stringFormat(
                        this.options.messages['ReportViewer_ErrorCreatingReportDocument'],
                        escapeHtml(this.getReport()),
                        escapeHtml(format)
                    )
                );
            });

        return documentId || '';
    }

    private getFormat(): string {
        if (this.options.viewMode === ViewMode.PrintPreview) {
            return 'HTML5';
        }

        return 'HTML5Interactive';
    }

    private getCanUsePlugin(): boolean {
        switch (this.options.printMode) {
            case PrintMode.ForcePDFFile:
                return false;
            case PrintMode.ForcePDFPlugin:
                return true;
            default:
                return this.printManager.getDirectPrintState(); // AUTO and 'undefined' states
        }
    }

    private createPreviewDeviceInfo(): DeviceInfo {
        let deviceInfo = this.createDeviceInfo();

        deviceInfo.ContentOnly = true;
        deviceInfo.UseSVG = true;

        return deviceInfo;
    }

    private createDeviceInfo(): DeviceInfo {
        let deviceInfo = new DeviceInfo();

        if (this.options.enableAccessibility) {
            deviceInfo.enableAccessibility = true;
            deviceInfo.contentTabIndex = this.getContentTabIndex();
        }

        let searchInitiated = this.getSearchInitiated();
        let searchMetadataOnDemand = this.options.searchMetadataOnDemand;

        deviceInfo.enableSearch = (!searchMetadataOnDemand || searchInitiated);

        return deviceInfo;
    }

    private getParameters(parameters: ParameterValuesType | undefined): Promise<any> {
        return this.serviceClient
            .getParameters(this.clientId, this.getReport(), parameters || this.parameterValues)
            .catch((error: RequestError) => {
                this.handleRequestError(error, this.options.messages['ReportViewer_UnableToGetReportParameters']);
            });
    }

    getReportParameters() : Record<any, {}> {
        const paramsToBeExposed = {};
        const processedParameterValues = this.getProcessedParameterValues();

        for (let key in processedParameterValues) {
            const processedParam = processedParameterValues[key];
            const paramValue = this.parameterValues[key];

            if (processedParam && processedParam.availableValues) {
                if (processedParam.multivalue) {
                    paramsToBeExposed[key] = addMultiComboParam(processedParam, paramValue, key);

                } else {
                    paramsToBeExposed[key] = addSingleComboParam(processedParam, paramValue, key);
                }
            } else {
                paramsToBeExposed[key] = paramValue;
            }
        }

        return paramsToBeExposed;
    }

    setProcessedParameterValues(values) {
        this.processedParameterValues = values;
    }

    getProcessedParameterValues() {
        return this.processedParameterValues;
    }

    private getDocumentInfo(raiseEvents: boolean, documentId: string): Promise<DocumentInfo> {
        if (raiseEvents) {
            this.emit('beginLoadReport');
        }

        return new Promise<DocumentInfo>((resolve: any, reject: any) => {
            let fn = async () => {
                if (this.cancelLoad) {
                    reject(this.options.messages['ReportViewer_RenderingCancelled']);
                } else {
                    // TODO this await is handling the case when the reportInstanceId is not already set
                    await this.registerInstancePromise;
                    this.serviceClient
                        .getDocumentInfo(this.clientId, this.reportInstanceId, documentId)
                        .then((info: DocumentInfo) => {
                            if (info && info.documentReady) {
                                resolve(info);
                            } else {
                                if (raiseEvents) {
                                    this.pageCount = info.pageCount;
                                    this.emit('reportLoadProgress', info);
                                }
                                window.setTimeout(fn, DocumentInfoPollIntervalMs);
                            }
                        })
                        .catch((error: RequestError) => this.handleRequestError(error, '', true));
                }
            };
            fn();
        });
    }

    private getPage(page: number): Promise<PageInfo> {
        return new Promise<PageInfo>((resolve: any) => {
            let fn = () => {
                this.serviceClient
                    .getPage(this.clientId, this.reportInstanceId, this.documentId, page)
                    .then((info: PageInfo) => {
                        if (info.pageReady) {
                            resolve(info);
                        } else {
                            window.setTimeout(fn, PagePollIntervalMs);
                        }
                    })
                    .catch((error: RequestError) => this.handleRequestError(error));
            };
            fn();
        });
    }

    private getPageForBookmark(nodes: BookmarkNode[] | null, id: string): number {
        if (nodes) {
            for (let i = 0, len = nodes.length; i < len; i++) {
                let node = nodes[i];
                if (node.id === id) {
                    return node.page;
                }
                let page = this.getPageForBookmark(node.items, id);
                if (page) {
                    return page;
                }

            }
        }
        return 0;
    }

    private fixDataContractJsonSerializer(arr: any): ParameterValuesType {
        let dict: ParameterValuesType = {};
        if (Array.isArray(arr)) {
            arr.forEach(function(pair) {
                dict[pair.Key] = pair.Value;
            });
        }
        return dict;
    }

    private execServerAction(actionId: string) {
        this.emit('serverActionStarted');

        this.refreshReportCore(false, this.documentId, actionId);
    }

    private onReportLoadComplete(info: DocumentInfo) {
        this.bookmarkNodes = info.bookmarkNodes;
        this.pageCount = info.pageCount;
        this.renderingExtensions = info.renderingExtensions;

        this.emitAsync('reportLoadComplete', info);
    }

    private onPageReady(info: PageInfo) {
        this.emit('pageReady', info);
    }

    private getReport(): string {
        return this.options.reportSource?.report || '';
    }

    getDocumentFormats(): Promise<RenderingExtension[] | void> | null {
        if (this.renderingExtensions) {
            return Promise.resolve(this.renderingExtensions);
        }

        if (!this.documentFormatsPromise) {
            this.documentFormatsPromise = this.serviceClient.getDocumentFormats().catch((error: RequestError) => {
                this.handleRequestError(error);
            });
        }

        return this.documentFormatsPromise;
    }

    setUIState(operationName: string, inProgress: boolean) {
        this.emit('setUIState', {
            operationName: operationName,
            inProgress: inProgress
        });
    }

    missingOrInvalidParameters(invalidParameters: ParameterInfo[]) {
        let errorMessage = this.options.messages['ReportViewer_MissingOrInvalidParameter'];

        invalidParameters.forEach((param: ParameterInfo) => {
            errorMessage += `\r\n${param.text} (${param.id})`;
        });

        this.raiseError(errorMessage);
        this.emit('missingOrInvalidParameters');
    }

    setSearchInitiated(searchInitiated: boolean) {
        this.searchInitiated = searchInitiated;
    }

    getSearchInitiated() {
        return this.searchInitiated;
    }

    setAiPromptInitiated(aiPromptInitiated: boolean) {
        this.aiPromptInitiated = aiPromptInitiated;
    }

    getAiPromptInitiated() {
        return this.aiPromptInitiated;
    }

    setContentTabIndex(tabIndex: number): void {
        this.contentTabIndex = tabIndex;
    }

    getContentTabIndex() {
        return this.contentTabIndex;
    }

    disposeSentinel() {
        this.stopSentinel();
        this.keepClientAliveSentinel = null;
    }

    getConfigurationInfo(): ConfigurationInfo {
        return this.configurationInfo;
    }

    setConfigurationInfo(configurationInfo: ConfigurationInfo): void {
        this.configurationInfo = configurationInfo;
    }

    getAiConfigurationOptions() {
        return this.configurationInfo.options.find(option => option.name === 'ai-insights');
    }

    shouldShowLicenseBanner() {
        const licenseData = this.configurationInfo?.license;

        return !licenseData?.isValid && this.loadFromSessionStorage('hideBanner') !== 'true';
    }

    shouldShowLicenseOverlay() {
        return !this.configurationInfo?.license?.isValid
    }

    saveToSessionStorage(key: string, value: string): void {
        sessionStorage.setItem(key, value)
    }

    loadFromSessionStorage(key: string): string {
        return sessionStorage.getItem(key);
    }

    removeFromSessionStorage(key: string): void {
        sessionStorage.removeItem(key);
    }

    clearSessionStorage(): void {
        // Not used at the moment and only added to complete the sessionStorage API wrapper
        // Avoid calling as it can break the user-configurable persistSession option (if enabled)
        sessionStorage.clear();
    }
}
